import React, { useState } from 'react';
import {
  ScrollView,
  Image,
  View,
  useWindowDimensions,
  StyleSheet,
  Platform,
} from 'react-native';
import Text from '../../../components/Text';
import Button from '../../../components/Button';
import Heading from '../../../components/Heading';
import HStack from '../../../components/HStack';
import { IMAGE_OPTIONS } from '../../../constants';
import AsterixDialog from '../../../components/AsterixDialog';
import RegisterActionBar from '../../../components/RegisterActionBar';
import RegisterPrevButton from '../../../components/RegisterPrevButton';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';
import { setRegisterData } from '../registerReducer';
import { residentApi } from '../../../api/resident';
import FeatherIcons from '@expo/vector-icons/Feather';
import { IDAnalyzer, RegistrationData } from '../types';
import ScreenWrapper from '../components/ScreenWrapper';
import isWeb from '../../../utils/isWeb';
import WebcamCapture from '../components/WebcamCapture';
import {
  handlePermissionError,
  formatDateToISO,
  pickAndroidImage,
} from '../../../utils';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { RootState } from '../../../redux/reducers';
import DragDropImageContainer from '../../../components/DragDropImage';
import {showErrorMessage} from '../../../service/flashMessage';

const DlCapture = ({ navigation }) => {
  const [loading, setLoading] = useState(false);
  const [dlDialogOpen, setDlDialogOpen] = useState(false);

  const registerValues =
    useSelector((state: RootState) => state?.register) || {};
  const _driversLicenseImage = registerValues?.driversLicenseImage;
  const [driversLicenseImage, setDriversLicenseImage] = useState(_driversLicenseImage);
  const dispatch = useDispatch();

  const { width } = useWindowDimensions();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const closeDialog = () => setDialogOpen(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const decodeDl = async (dlImage: string) => {
    setLoading(true);
    try {
      const res = await residentApi().post('/idAnalyzer', {
        imageBase64: dlImage,
      });
      const result = res.data;

      if (result.errors) {
        throw new Error(result?.errors[0]);
      } else {
        const dlData: IDAnalyzer = result.data;
        console.log('✅ DlCapture - DL data received from API:', dlData);
        if (dlData) {
          const registrationData: RegistrationData = {
            firstName: dlData?.firstName,
            lastName: dlData?.lastName,
            driversLicenseImage: driversLicenseImage,
            addressLine1: dlData?.address1,
            addressLine2: dlData?.address2,
            dlNumber: dlData?.documentNumber,
            dateOfBirth: formatDateToISO(dlData?.dob),
            driversLicenseExpiry: formatDateToISO(dlData?.expiry),
            gender: dlData?.sex,
          };
          console.log('✅ DlCapture - Data captured from DL:', {dateOfBirth: formatDateToISO(dlData?.dob), expiration: formatDateToISO(dlData?.expiry), dlNumber: dlData?.documentNumber});
          // @ts-ignore
          dispatch(setRegisterData(registrationData));
          navigation.navigate('Name');
        } else {
          setDlDialogOpen(true);
        }
      }
    } catch (error) {
      throw new Error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const showGalleryPicker = async () => {
    if (isWeb) {
      //@ts-ignore
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/png, image/jpeg';

      input.onchange = e => {
        const file = e.target.files[0];
        const reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = event => {
          const data = event.target.result;
          //@ts-ignore
          setDriversLicenseImage(data.split(',')[1]);
          setCameraError(null);
        };
      };

      input.click();
    } else if (Platform.OS === 'android') {
      const result = await pickAndroidImage();

      if (result.cancelled) {
        return;
      }

      if (result.error || !result.base64) {
        showErrorMessage(
          result.error?.message || 'Unable to select an image from your device.',
        );
        return;
      }

      setDriversLicenseImage(result.base64);
      setCameraError(null);
    } else {
      const ImagePicker = require('react-native-image-crop-picker').default;
      ImagePicker.openPicker(IMAGE_OPTIONS).then(image => {
        //@ts-ignore
        setDriversLicenseImage(image.data);
        setCameraError(null);
      }).catch((e) => handlePermissionError(e, 'gallery'));
    }
  };

  const showCameraPicker = () => {
    const ImagePicker = require('react-native-image-crop-picker').default;
    ImagePicker.openCamera(IMAGE_OPTIONS).then(image => {
      //@ts-ignore
      setDriversLicenseImage(image.data);
      setCameraError(null);
    }).catch((e) => handlePermissionError(e, 'camera'));
  };

  const clearPicture = () => {
    setDriversLicenseImage(null);
    setDlDialogOpen(false);
  }

  const onRetake = () => {
    setDriversLicenseImage(null);
    setDialogOpen(false);
  }

  const continueWithPicture = () => {
    const registrationData = {
      driversLicenseImage: driversLicenseImage,
    }

    // @ts-ignore
    dispatch(setRegisterData(registrationData));
    setDlDialogOpen(false);

    navigation.navigate('Name');
  }

  return (
    <ScreenWrapper alignTop>
      <SafeAreaView
        style={[
          styles.flex1,
          styles.margin4,
          isWeb && {backgroundColor: '#ffffff', width: width / 3},
        ]}>
        <RegisterActionBar step={5} />
        <ScrollView style={styles.marginX2}>
          <View style={styles.marginX6}>
            <Heading size="lg">Driver's License Photo</Heading>
            <Text style={[styles.marginTop8, styles.textLg]}>
              Please load or take a picture of the front face of your Drivers
              License. From your drivers licence information we will save:
            </Text>
            <HStack style={styles.marginLeft4}>
              <FeatherIcons
                style={styles.marginTop3}
                name="check-square"
                size={15}
                color="green"
              />
              <Text style={styles.marginTop2}>Your name.</Text>
            </HStack>
            <HStack style={styles.marginLeft4}>
              <FeatherIcons
                style={styles.marginTop3}
                name="lock"
                size={15}
                color="green"
              />
              <Text style={styles.marginTop2}>
                Your drivers licence number (Encrypted).
              </Text>
            </HStack>
            <HStack style={styles.marginLeft4}>
              <FeatherIcons
                style={styles.marginTop3}
                name="calendar"
                size={15}
                color="green"
              />
              <Text style={styles.marginTop2}>Birthday</Text>
            </HStack>
            <HStack style={styles.marginLeft4}>
              <FeatherIcons
                style={styles.marginTop3}
                name="check-square"
                size={15}
                color="green"
              />
              <Text style={styles.marginTop2}>
                Expiration date (encrypted, so we know when it needs
                to be renewed).
              </Text>
            </HStack>
            <Text
              style={{
                marginTop: 8,
                fontSize: 20,
                fontWeight: 'bold',
              }}>
              This profile will not be approved by management if the driver's
              license is not provided.
            </Text>
            <View style={styles.marginTop8}>
              {driversLicenseImage ? (
                <View>
                  <View style={styles.licenseUploadContainer}>
                    {driversLicenseImage !== null && (
                      <Image
                        source={{
                          uri: `data:image/jpeg;base64,${driversLicenseImage}`,
                        }}
                        style={styles.roundedImage}
                        resizeMode="contain"
                      />
                    )}
                  </View>
                </View>
              ) : (
                // <View style={styles.licenseUploadContainer} />
                <DragDropImageContainer value={driversLicenseImage} onChange={setDriversLicenseImage} height={256} />
              )}
            </View>
            {!!cameraError && (
              <Text style={styles.cameraErrorText}>
                {cameraError}
              </Text>
            )}
          </View>
        </ScrollView>
        <HStack style={styles.margin2}>
          {isWeb ? (
            <View style={{flex: 1}}>
              <WebcamCapture
                onCapture={imgData => {
                  setDriversLicenseImage(imgData);
                  setCameraError(null);
                }}
                onCameraError={message => setCameraError(message)}
              />
            </View>
          ) : (
            <Button
              style={[styles.flex1]}
              variant="ghost"
              size="lg"
              py="4"
              onPress={() => showCameraPicker()}>
              Take picture
            </Button>
          )}
          <Button
            style={[styles.flex1,]}
            variant="ghost"
            size="lg"
            py="4"
            onPress={() => {
              void showGalleryPicker();
            }}>
            {isWeb ? 'Choose file' : 'Choose from gallery'}
          </Button>
        </HStack>
        <HStack style={styles.margin2}>
          <RegisterPrevButton />
          <Button
            isLoading={loading}
            py="4"
            isLoadingText="Extracting data from DL. Please wait ..."
            onPress={() =>
              !driversLicenseImage
                ? setDialogOpen(true)
                : decodeDl(driversLicenseImage)
            }
            style={[styles.flex1]}>
            Next
          </Button>
        </HStack>
      </SafeAreaView>
      <ConfirmationDialog
        isOpen={dialogOpen}
        onClose={closeDialog}
        onNoPressed={closeDialog}
        onYesPressed={() => navigation.navigate('Name')}
        title="Are you sure you wish to skip this step?"
        description="This profile will not be approved by management if this information is not provided."
      />
      <AsterixDialog
        message={'Extracting data from DL ...'}
        isSwitching={loading}
      />
      <ConfirmationDialog
        isOpen={dlDialogOpen}
        onClose={() => setDlDialogOpen(false)}
        onYesPressed={onRetake}
        title="Error"
        description="Your drivers license cannot be read."
        negativeLabel="Continue"
        centerButton
        centerButtonLabel="Clear picture"
        onCenterButtonPressed={clearPicture}
        positiveLabel="Retake"
        onNoPressed={continueWithPicture}
      />
    </ScreenWrapper>
  );
};
const styles = StyleSheet.create({
  flex1: {
    flex: 1,
  },
  cameraErrorText: {
    color: '#b91c1c',
    marginTop: 12,
    fontSize: 15,
  },
  margin4: {
    margin: 16,
  },
  marginX2: {
    marginHorizontal: 8,
    marginBottom: 16,
  },
  marginX6: {
    marginHorizontal: 24,
    marginTop: 16,
  },
  marginTop8: {
    marginTop: 32,
  },
  textLg: {
    fontSize: 18,
  },
  marginLeft4: {
    marginLeft: 16,
    marginTop: 8,
  },
  marginTop3: {
    marginTop: 12,
    marginRight: 8,
  },
  marginTop2: {
    marginTop: 8,
    fontSize: 18,
  },
  licenseUploadContainer: {
    height: 256,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
  },
  roundedImage: {
    borderRadius: 8,
    height: 256,
    width: '99%',
  },
  margin2: {
    margin: 8,
  },
  marginTop6: {
    marginTop: 24,
  },
  marginTop4: {
    marginTop: 16,
  },
});

export default DlCapture;
